import React from "react";
import get from "lodash/get";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { AccessType } from "@quintype/components";
import { StoryTags } from "../../story-page-components/story-tags.js";
import { PhotoStoryCard } from "../../story-page-components/photo-story-card.js";
import { addTargetBlankToExtLinks } from "../../../../utils/add-target-blank-to-extlinks";
import { getPrimaryColor, getSocialShareStyle, getOverride, selectedStoryTemplate, accessCheck } from "../../../utils";
import { getAccessData, handleAccess, checkIfStoryPublic } from "../../sso-components/sso-utils";
import { Author } from "../../basic/author.js";
import { HeroImage } from "../../atoms/hero-image";
import { HeroImageCaption } from "../../atoms/hero-image-caption";
import { StorySubheadline } from "../../atoms/story-subheadline";
import { Sidebar } from "../../molecules/sidebar";
import { StoryFooter } from "../../molecules/story-footer";
import { StoryHeadline } from "../../atoms/story-headline";
import { StorySection } from "../../atoms/story-section";
import { Share } from "../../molecules/share";
import { AmazonPolly } from "../../amazon-polly/amazon-polly";
import { AdService } from "../../ad-service.js";

import "./photo.module.css";

const photoStoryClass = {
  "hero-priority": "hero-priority-template",
  "headline-priority": "headline-priority-template"
};
const getPhotoStoryClass = new Proxy(photoStoryClass, getOverride(""));

const getSideAd = new Proxy({ "hero-priority": false }, getOverride(true));

const aspectRatio = {
  "hero-priority": [16, 9],
  "headline-priority": [16, 9]
};

const desktopAspectRatio = new Proxy(aspectRatio, getOverride([8, 3]));

const mobileAspectRatio = new Proxy({}, getOverride([16, 9]));

function getStoryCardTemplate(cards = [], story, config, template, firstStory) {
  const cdnImage = get(config, ["cdn-image"], "");
  const socialShareStyle = getSocialShareStyle(config);
  const primaryColor = getPrimaryColor(config);
  const hasMultiCards = cards.length > 1;
  const infiniteScrollStyle = get(config, ["theme-attributes", "infiniteScrollStyle"]);
  const firstStoryCard = get(story, ["cards", 0]);

  if (infiniteScrollStyle === "read-more" && !firstStory) {
    return (
      <PhotoStoryCard
        key={firstStoryCard.id}
        card={firstStoryCard}
        story={story}
        cdn={cdnImage}
        firstStory={firstStory}
        config={config}
        primaryColor={primaryColor}
        socialShareGrey={socialShareStyle.includes("grey")}
        template={template}
      />
    );
  } else {
    return cards.map((card, index) => {
      if (index === 0 && hasMultiCards) {
        return (
          <>
            <PhotoStoryCard
              key={card.id}
              card={card}
              story={story}
              cdn={cdnImage}
              config={config}
              primaryColor={primaryColor}
              firstStory={firstStory}
              socialShareGrey={socialShareStyle.includes("grey")}
              template={template}
            />
            <div className="app-ad app-ad--story-horizontal grid-column-full">
              <AdService adtype="Story-Middle-Ad" />
            </div>
          </>
        );
      } else {
        return (
          <PhotoStoryCard
            key={card.id}
            card={card}
            story={story}
            cdn={cdnImage}
            config={config}
            primaryColor={primaryColor}
            firstStory={firstStory}
            socialShareGrey={socialShareStyle.includes("grey")}
            template={template}
          />
        );
      }
    });
  }
}

function PhotoStoryTemplate({ story = {}, config, preview, access, accessLoading, firstStory }) {
  const photoStory = selectedStoryTemplate("photo-story", story, config);
  const socialShareStyle = getSocialShareStyle(config);
  const authorsArray = get(story, ["authors"]) || [{ name: story["author-name"], id: story["author-id"] }];
  const publishedTime = story["last-published-at"] || story["first-published-at"];
  const isStoryPublic = checkIfStoryPublic(story);
  const storyCards = get(story, ["cards"], []);
  const photoStoryList = { "hero-priority": true, "headline-priority": true, default: false };
  const horizontalShare = photoStoryList[photoStory] && socialShareStyle.includes("horizontal");
  const storyId = get(story, ["id"]);
  const showPolly = get(access, [storyId, "granted"], false);

  function getSocialShare(photoStory) {
    switch (photoStory) {
      case "headline-priority":
        return <Share story={story} config={config} grey={socialShareStyle.includes("grey")} vertical />;
      case "hero-priority":
        return (
          <Share
            story={story}
            config={config}
            grey={socialShareStyle.includes("grey")}
            vertical={socialShareStyle.includes("vertical")}
          />
        );
      default:
        return <Share story={story} config={config} grey={socialShareStyle.includes("grey")} />;
    }
  }

  /* Hero Image */
  const supportImageGrid = {
    default: "grid-col-1-15",
    "hero-priority": "grid-col-2-14",
    "headline-priority": "grid-col-2-10 grid-row-5-6"
  };

  const imageStyle = supportImageGrid[photoStory] ? supportImageGrid[photoStory] : supportImageGrid["default"];

  /* Hero Image Caption */
  const supportImageCaptionGrid = {
    default: "grid-col-2-14",
    "hero-priority": "grid-col-2-14",
    "headline-priority": "grid-col-2-10 grid-row-6-7"
  };

  const imageCaptionStyle = supportImageCaptionGrid[photoStory]
    ? supportImageCaptionGrid[photoStory]
    : supportImageCaptionGrid["default"];

  /* Section */
  const supportSectionGrid = {
    default: "grid-col-2-10",
    "hero-priority": "grid-col-4-12",
    "headline-priority": "grid-col-2-10 grid-row-1-2"
  };

  const sectionStyle = supportSectionGrid[photoStory] ? supportSectionGrid[photoStory] : supportSectionGrid["default"];

  /* Headline */
  const supportHealineGrid = {
    default: "grid-col-2-10",
    "hero-priority": "grid-col-4-12",
    "headline-priority": "grid-col-2-10 grid-row-2-3"
  };

  const headlineStyle = supportHealineGrid[photoStory] ? supportHealineGrid[photoStory] : supportHealineGrid["default"];

  /* Sub-Headline */

  const supportSubheadlineGrid = {
    default: "grid-col-2-10",
    "hero-priority": "grid-col-4-12",
    "headline-priority": "grid-col-2-10 grid-row-3-4"
  };
  const subheadlineStyle = supportSubheadlineGrid[photoStory]
    ? supportSubheadlineGrid[photoStory]
    : supportSubheadlineGrid["default"];

  /* Amazon Polly */

  const supportAmazonPollyGrid = {
    default: "grid-col-2-10",
    "hero-priority": "grid-col-4-12",
    "headline-priority": "grid-col-2-10 grid-row-4-5"
  };

  const amazonPollyStyle = supportAmazonPollyGrid[photoStory]
    ? supportAmazonPollyGrid[photoStory]
    : supportAmazonPollyGrid["default"];

  /* Tags, Footer, Horizontal-Line */
  const supportHeaderGrid = {
    default: "grid-col-2-10",
    "hero-priority": "grid-col-4-12",
    "headline-priority": "grid-col-3-10"
  };

  const headerStyle = supportHeaderGrid[photoStory] ? supportHeaderGrid[photoStory] : supportHeaderGrid["default"];

  /* Horizontal Social-Share Author */
  const supportHorizontalAuthorGrid = {
    default: "grid-col-2-7",
    "hero-priority": "grid-col-4-8",
    "headline-priority": "grid-col-3-7 grid-row-7-8"
  };
  const horizontalAuthorStyle = supportHorizontalAuthorGrid[photoStory]
    ? supportHorizontalAuthorGrid[photoStory]
    : supportHorizontalAuthorGrid["default"];

  /* Vertical Social-Share Author */
  const supportAuthorGrid = {
    default: "grid-col-2-7",
    "hero-priority": "grid-col-4-12",
    "headline-priority": "grid-col-3-10 grid-row-7-8"
  };

  const authorStyle = supportAuthorGrid[photoStory] ? supportAuthorGrid[photoStory] : supportAuthorGrid["default"];

  /* Horizontal Social-Share */
  const supportHorizontalShareGrid = {
    default: "grid-col-7-10",
    "hero-priority": "grid-col-8-12",
    "headline-priority": "grid-col-2-3 grid-row-7-auto"
  };

  const horizontalShareStyle = supportHorizontalShareGrid[photoStory]
    ? supportHorizontalShareGrid[photoStory]
    : supportHorizontalShareGrid["default"];

  /* Vertical Social-Share */
  const supportVerticalShareGrid = {
    default: "grid-col-7-10",
    "hero-priority": "grid-col-2-3 grid-row-3-auto",
    "headline-priority": "grid-col-2-3 grid-row-7-auto"
  };

  const verticalShareStyle = supportVerticalShareGrid[photoStory]
    ? supportVerticalShareGrid[photoStory]
    : supportVerticalShareGrid["default"];

  /* Card */
  const supportCardGrid = {
    default: "grid-col-2-10",
    "hero-priority": "grid-col-3-13",
    "headline-priority": "grid-col-3-10"
  };

  const cardStyle = supportCardGrid[photoStory] ? supportCardGrid[photoStory] : supportCardGrid["default"];

  /* Sidebar and Related-Stories */
  const supportSidebarGrid = {
    default: "grid-col-10-14 grid-row-4-11",
    "hero-priority": "grid-col-2-14",
    "headline-priority": "grid-col-10-14 grid-row-1-13"
  };

  const sidebarStyle = supportSidebarGrid[photoStory] ? supportSidebarGrid[photoStory] : supportSidebarGrid["default"];
  const readTime = get(story, ["read-time"]);
  return (
    <article
      className="blank-story grid"
      styleName={`wrapper ${getPhotoStoryClass[photoStory]} ${horizontalShare ? "horizontal" : ""}`}
    >
      {/* Hero Image */}
      <div styleName="hero-image" className={imageStyle} data-testid="photo-story-hero-image">
        <HeroImage
          story={story}
          desktopAspectRatio={desktopAspectRatio[photoStory]}
          mobileAspectRatio={mobileAspectRatio[photoStory]}
        />
      </div>
      {/* Hero Image Caption */}
      <div styleName="grid-item hero-image-caption" className={imageCaptionStyle}>
        <HeroImageCaption story={story} />
      </div>
      {/* Section */}
      <div styleName="grid-item" className={sectionStyle}>
        <StorySection story={story} config={config} />
      </div>
      {/* Headline */}
      <div styleName="grid-item headline" className={headlineStyle}>
        <StoryHeadline headline={story.headline} />
      </div>
      {/* Subheadline */}
      <div styleName="grid-item subheadline" className={subheadlineStyle}>
        <StorySubheadline subheadline={story.subheadline} />
      </div>
      {/* Amazon Polly */}
      {showPolly && (
        <div styleName="grid-item amazon-polly-wrapper" className={amazonPollyStyle}>
          <AmazonPolly story={story} />
        </div>
      )}
      {/* Author */}
      <div styleName="grid-item author" className={`${horizontalShare ? horizontalAuthorStyle : authorStyle}`}>
        <Author config={config} authors={authorsArray} isLink showDateOnly date={publishedTime} readTime={readTime} />
      </div>
      {/* Social Share */}
      <div styleName="grid-item share" className={`${horizontalShare ? horizontalShareStyle : verticalShareStyle}`}>
        <div styleName="sticky">{getSocialShare(photoStory)}</div>
      </div>
      {/* Horizontal Line */}
      <div styleName="grid-item hr" className={headerStyle} />
      {/* Story Cards */}
      <div styleName="grid-item cards" className={cardStyle}>
        {isStoryPublic
          ? getStoryCardTemplate(storyCards, story, config, photoStory, firstStory)
          : handleAccess(story, access, accessLoading, config, cards =>
              getStoryCardTemplate(cards, story, config, photoStory, firstStory)
            ) /* Method to check for access of the user */}
      </div>

      {/* Story Tags */}
      <div styleName="grid-item" className={headerStyle}>
        <StoryTags tags={story.tags} config={config} />
      </div>
      {/* Sidebar */}
      <div styleName="grid-item sidebar" className={sidebarStyle}>
        <Sidebar story={story} showAd={getSideAd[photoStory]} template={photoStory} wide />
      </div>
      {/* Story Footer */}
      <div styleName="grid-item footer" className={headerStyle}>
        {!preview && <StoryFooter story={story} />}
      </div>
    </article>
  );
}

PhotoStoryTemplate.propTypes = {
  story: PropTypes.object,
  firstStory: PropTypes.bool,
  config: PropTypes.object,
  preview: PropTypes.bool,
  access: PropTypes.object,
  accessLoading: PropTypes.bool
};

class PhotoStoryAccessWrapper extends React.Component {
  componentDidMount() {
    const { checkAccess, story, accessUpdated, accessIsLoading, config } = this.props;
    getAccessData(checkAccess, story, accessUpdated, accessIsLoading, config);
    addTargetBlankToExtLinks(this.storyWrapperElement);
  }
  render() {
    return (
      <div className="story-grid story-grid--photo" ref={el => (this.storyWrapperElement = el)}>
        <PhotoStoryTemplate {...this.props} />
      </div>
    );
  }
}

PhotoStoryAccessWrapper.propTypes = {
  checkAccess: PropTypes.func,
  story: PropTypes.object,
  accessUpdated: PropTypes.func,
  accessIsLoading: PropTypes.func,
  config: PropTypes.object
};

class PhotoStoryBase extends React.Component {
  render() {
    const email = get(this.props, ["member", "email"]);
    const phone = get(this.props, ["member", "metadata", "phone-number"], "1234");
    const accessTypeKey = get(this.props, ["config", "theme-attributes", "accesstypeKey"]);
    const disableMetering = get(this.props, ["config", "theme-attributes", "disableMetering"]) === "true";
    const isStaging = get(this.props, ["config", "theme-attributes", "accesstypeEnvironment"]) === "staging";
    const enableAccesstype = get(this.props, ["config", "theme-attributes", "enableAccesstype"], false);

    return (
      <AccessType
        enableAccesstype={accessCheck(enableAccesstype)}
        isStaging={isStaging}
        accessTypeKey={accessTypeKey} // "9rm1ijqQmGfJ2kwQ9zt2gWhK"
        email={email}
        phone={phone}
        disableMetering={disableMetering}
      >
        {({ initAccessType, checkAccess, accessUpdated, accessIsLoading }) => (
          <PhotoStoryAccessWrapper
            accessIsLoading={accessIsLoading}
            accessUpdated={accessUpdated}
            initAccessType={initAccessType}
            checkAccess={checkAccess}
            {...this.props}
          />
        )}
      </AccessType>
    );
  }
}

const mapStateToProps = state => ({
  config: get(state, ["qt", "config"], {}),
  member: get(state, ["member"], null),
  access: get(state, ["access"], null),
  accessLoading: get(state, ["accessLoading"], true)
});

export const PhotoStory = connect(mapStateToProps)(PhotoStoryBase);
